
import { Component, Prop } from 'vue-property-decorator';
import { OrderType } from '@/enums/OrderType';
import { SubOrderType } from '@/enums/SubOrderType';
import { TipType } from '@/enums/TipType';
import { Venue } from '@/interfaces/models/Venue';
import { DefaultTip } from '@/interfaces/models/DefaultTip';
import { Tip } from '@/interfaces/models/VenueTip';
import { SelectItem } from '@/interfaces/components/SelectItem';
import StackedForm from '@/mixins/StackedForm';
import { mixins } from 'vue-class-component';
import { ValidationObserver } from 'vee-validate';
import _ from 'lodash';

@Component({
  components: { ValidationObserver },
})
export default class TipsForm extends mixins(StackedForm) {
  public $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  public tips: Tip[] = [];
  public subOrderTypes: SubOrderType[] = Object.values(SubOrderType) || [];

  get initValues(): Tip[] | [] {
    const venue: Venue = this.initialValues as Venue;
    const decimal = (value: any) => value?.$numberDecimal || value || '';

    return (venue?.tips || []).map((tip: Tip) => {
      return {
        orderType: tip.orderType || OrderType.PREORDER,
        subOrderTypes: tip.subOrderTypes || [],
        enabled: tip.enabled || false,
        defaultTips: (tip.defaultTips || []).map((defaultTip: DefaultTip) => ({
          value: decimal(defaultTip.value),
          type: defaultTip.type || TipType.ABSOLUTE,
          isDefault: defaultTip.isDefault || false,
        })),
      };
    });
  }

  get orderTypes(): OrderType[] {
    return [OrderType.STANDARD, OrderType.PREORDER, OrderType.TERMINAL];
  }

  get tipTypes(): SelectItem[] {
    return Object.values(TipType).map((val: TipType) => ({ value: val, text: this.$t(`venue.form.tipTypes.${val}`) }));
  }

  public mounted() {
    this.tips = _.cloneDeep(this.initValues);
  }

  public addTip() {
    if (this.tips.length >= 4) {
      return;
    }

    this.tips.push({
      orderType: OrderType.PREORDER,
      subOrderTypes: [],
      enabled: false,
      defaultTips: [],
    });
  }

  public removeTip(index: number) {
    if (this.tips.length <= index) {
      return;
    }

    this.tips.splice(index, 1);
  }

  public addDefaultTip(tipIndex: number) {
    if (!this.tips[tipIndex]) {
      return;
    }

    this.tips[tipIndex].defaultTips.push({
      value: 0,
      type: TipType.ABSOLUTE,
      isDefault: false,
    });
  }

  public removeDefaultTip(tipIndex: number, defaultTipIndex: number) {
    if (
      !this.tips[tipIndex] ||
      !this.tips[tipIndex].defaultTips ||
      this.tips[tipIndex].defaultTips.length <= defaultTipIndex
    ) {
      return;
    }

    this.tips[tipIndex].defaultTips.splice(defaultTipIndex, 1);
  }

  public defaultTipValueRules(tip: DefaultTip) {
    return `${tip.type === TipType.RELATIVE ? 'decimal|smaller_eq_than:0.2|' : ''}greater_eq_than:0`;
  }

  public getAvailableSubOrderTypes(orderType: OrderType): SubOrderType[] {
    switch (orderType) {
      case OrderType.STANDARD:
        return [];
      case OrderType.PREORDER:
        return this.subOrderTypes;
      case OrderType.TERMINAL: {
        const allowed = [SubOrderType.INSIDE, SubOrderType.TAKE_AWAY];
        return this.subOrderTypes.filter((s) => allowed.includes(s));
      }
      default:
        return [];
    }
  }

  public subOrderTypesByOrderType(orderType: OrderType = OrderType.PREORDER, currentTipIndex?: number): SubOrderType[] {
    const used = new Set<SubOrderType>();
    let currentSelected: SubOrderType[] = [];
    let available: SubOrderType[];

    this.tips.forEach((tip, idx) => {
      if (tip.orderType === orderType && idx !== currentTipIndex) {
        (tip.subOrderTypes ?? []).forEach((s: SubOrderType) => used.add(s));
      }
    });

    if (typeof currentTipIndex === 'number' && this.tips[currentTipIndex]) {
      currentSelected = this.tips[currentTipIndex].subOrderTypes ?? [];
    }

    available = this.getAvailableSubOrderTypes(orderType);

    const result = available.filter((s) => !used.has(s) || currentSelected.includes(s));

    currentSelected.forEach((s) => {
      if (!result.includes(s)) {
        result.push(s);
      }
    });

    return result;
  }

  public IsDefaultTipFlagUsedForOrderType(tipIndex: number, defaultTipIndex: number): boolean {
    const currentTip = this.tips[tipIndex];

    if (!currentTip || !Array.isArray(currentTip.defaultTips)) {
      return false;
    }

    const currentDefaultTip = currentTip.defaultTips[defaultTipIndex];

    if (!currentDefaultTip) {
      return false;
    }

    // For each subOrderType in the current tip, check if any other tip's defaultTip
    // has isDefault for the same subOrderType
    for (const subOrderType of currentTip.subOrderTypes || []) {
      for (let i = 0; i < this.tips.length; i++) {
        const tip = this.tips[i];

        if (!Array.isArray(tip.subOrderTypes) || !Array.isArray(tip.defaultTips)) {
          continue;
        }

        if (i === tipIndex) {
          // Check other defaultTips in the same tip
          for (const [j, defaultTip] of tip.defaultTips.entries()) {
            if (j === defaultTipIndex) {
              continue;
            }

            if (tip.subOrderTypes.includes(subOrderType) && defaultTip.isDefault) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  public isDefaultTipCheckboxDisabled(tipIndex: number, defaultTipIndex: number): boolean {
    return this.IsDefaultTipFlagUsedForOrderType(tipIndex, defaultTipIndex);
  }

  public onDefaultTipTypeChange(tipIndex: number, defaultTipIndex: number) {
    const currentTip = this.tips[tipIndex];
    const currentDefaultTip = currentTip.defaultTips[defaultTipIndex];

    if (currentDefaultTip.type === TipType.ANY) {
      currentDefaultTip.value = undefined;
    }
  }

  public onTipSubOrderTypeChange(tipIndex: number) {
    const currentTip = this.tips[tipIndex];

    if (!currentTip || !Array.isArray(currentTip.defaultTips)) {
      return;
    }

    currentTip.defaultTips.forEach((defaultTip) => {
      if (this.IsDefaultTipFlagUsedForOrderType(tipIndex, currentTip.defaultTips.indexOf(defaultTip))) {
        defaultTip.isDefault = false;
      }
    });
  }

  public validate() {
    return this.$refs.observer.validate();
  }

  public getData() {
    const tips = _.cloneDeep(this.tips).map((tip: Tip) => {
      return {
        ...tip,
        defaultTips: (tip.defaultTips || []).map((defaultTip: DefaultTip) => {
          if (!defaultTip.value && defaultTip.value !== 0 && defaultTip.type === TipType.ANY) {
            const { type } = defaultTip;
            return { type };
          }

          return defaultTip;
        }),
      };
    });

    return {
      tips,
    };
  }
}
